import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';

// Renders a lucide icon by (kebab or Pascal) name, falling back gracefully.
export function Icon({ name, className = 'w-5 h-5', ...rest }) {
  const pascal = String(name || 'circle')
    .split(/[-_ ]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const Cmp = Icons[pascal] || Icons.Circle;
  return <Cmp className={className} {...rest} />;
}

export function Pager({ page, pages, onPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-8" data-testid="pager">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="gx-btn-ghost text-sm disabled:opacity-40" data-testid="pager-prev">← Prev</button>
      <span className="font-mono text-sm text-gx-muted">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="gx-btn-ghost text-sm disabled:opacity-40" data-testid="pager-next">Next →</button>
    </div>
  );
}

export function SectionHead({ eyebrow, eyebrowIcon, color = 'cyan', title, viewAll, viewAllLabel = 'View all →' }) {
  const colorMap = {
    cyan: 'bg-gx-cyan/10 border-gx-cyan/25 text-gx-cyan',
    purple: 'bg-gx-purple/10 border-gx-purple/25 text-gx-purple',
    emerald: 'bg-emerald-400/10 border-emerald-400/25 text-emerald-400',
  };
  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        {eyebrowIcon && (
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center border ${colorMap[color]}`}>
            <Icon name={eyebrowIcon} className="w-4 h-4" />
          </span>
        )}
        <p className="gx-eyebrow">{eyebrow}</p>
      </div>
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">{title}</h2>
        {viewAll && <Link to={viewAll} className="text-sm text-gx-muted hover:text-gx-cyan font-display transition">{viewAllLabel}</Link>}
      </div>
    </>
  );
}

export function PageHero({ eyebrow, title, holo, subtitle, children, glow = 'cyan' }) {  return (
    <div className="relative rounded-3xl overflow-hidden mb-8 border border-white/[0.06] bg-[#070A13]">
      <div className="absolute inset-0 pointer-events-none opacity-45" style={{ background: 'radial-gradient(circle at 18% 20%, rgba(0,229,255,0.2), transparent 42%),radial-gradient(circle at 85% 90%, rgba(109,40,255,0.18), transparent 50%)' }} />
      <div className="relative p-7 md:p-10 flex flex-wrap items-end justify-between gap-5">
        <div>
          {eyebrow && <p className="text-xs text-gx-cyan font-display tracking-[0.3em] uppercase mb-2">{eyebrow}</p>}
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight">{title} {holo && <span className="gx-holo-text">{holo}</span>}</h1>
          {subtitle && <p className="text-gx-muted mt-3 max-w-xl text-base md:text-lg">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}
